<script lang="ts" setup>
import { ref } from "vue";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { app } from "@/fire";
import { string } from "yup";
import { useForm, useField } from "vee-validate";
import { useI18n } from "vue-i18n";

const emits = defineEmits(["back"]);

const props = defineProps({
  email: {
    type: String,
    required: true,
  },
});

const auth = getAuth(app);
const { t } = useI18n();

const { validate, errors } = useForm();
const { value: email } = useField<string>(
  "email",
  string()
    .email(t("login.email.error.invalid"))
    .required(t("login.email.error.missing")),
  { initialValue: props.email }
);

const hasValidated = ref(false);
const emailIsSent = ref(false);

const sendResetLink = async () => {
  hasValidated.value = true;
  const { valid } = await validate();
  if (!valid) {
    return;
  }

  try {
    await sendPasswordResetEmail(auth, email.value);
    emailIsSent.value = true;
  } catch (e) {
    console.error(e);
  }
};
</script>

<template>
  <div class="login_container" v-if="!emailIsSent">
    <a class="link top" @click="emits('back')">
      <span class="material-symbols-outlined"> arrow_back </span>
      <span class="text">
        {{ $t("login.back") }}
      </span>
    </a>

    <h2 class="login_header">
      {{ $t("login.passwordReset.header") }}
    </h2>

    <div class="subheader">
      <div>{{ $t("login.passwordReset.subtitle") }}</div>
    </div>

    <form @submit.prevent="sendResetLink">
      <div class="input_container">
        <label>{{ $t("login.email.label") }}</label>
        <input
          class="input"
          :class="{ invalid: errors.email !== undefined && hasValidated }"
          v-model="email"
          type="email"
          :placeholder="$t('login.email.placeholder')"
          autocomplete="username"
        />
        <p
          class="help"
          :class="{ visible: errors.email !== undefined && hasValidated }"
        >
          {{ errors.email }}
        </p>
      </div>

      <button class="primary button" type="submit">
        {{ $t("login.passwordReset.submit") }}
      </button>
    </form>
  </div>

  <div v-else class="login_container">
    <div class="center">
      <span class="material-symbols-outlined success_icon"> mail </span>

      <h2 class="login_header">
        {{ $t("login.passwordReset.sent.header") }}
      </h2>

      <div class="subheader">
        <div>{{ $t("login.passwordReset.sent.subtitle") }}</div>
      </div>

      <button class="button center" @click="emailIsSent = false">
        {{ $t("login.passwordReset.resend") }}
      </button>
    </div>
  </div>
</template>

<style src="./login.scss" scoped lang="scss"></style>

<style lang="scss" scoped>
.subheader {
  font-size: 0.9em;
  margin: 1em 0 2em 0;
}

.button.center,
.center {
  text-align: center;
  justify-content: center;
}

.material-symbols-outlined.success_icon {
  color: var(--classic-success, #48c78e);
  border: 1px solid var(--classic-success, #48c78e);
  border-radius: 50%;
  padding: 0.3em;
  font-size: 2.5em;
  margin-bottom: 1em;
}
</style>
